import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import css from './SectionFilter.module.css';
import { useConfiguration } from '../../../../context/configurationContext';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { sortCategoriesByName } from '../../../EditListingPage/EditListingWizard/EditListingDetailsPanel/EditListingDetailsForm';
import {
  capacityToRangeOptions,
  categoryToOptions,
  categoryToRangeOptions,
  checkoutStyles,
  configToOptions,
  mileageToRangeOptions,
  powerToRangeOptions,
  priceToRangeOptions,
  toSelectFormat,
} from './utils';
import YearFilter from './YearFilter';
import MileageFilter from './MileageFilter';
import PowerFilter from './PowerFilter';
import CapacityFilter from './CapacityFilter';
import { popularBrands, regionOptions } from './consts';
import ExtendedFilterBtn from './ExtendedFilterBtn';
/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show article content.
 * The article content is mainly supposed to be inside a block.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'article'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {boolean?} props.isInsideContainer
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionFilter = () => {
  const [selectValues, setSelectValues] = useState({
    brandOption: null,
    modelOption: null,
    yearFromOption: null,
    yearToOption: null,
    mileageFromOption: null,
    mileageToOption: null,
    engineOption: null,
    powerFromOption: null,
    powerToOption: null,
    capacityFromOption: null,
    capacityToOption: null,
    driveTypeOption: null,
    priceOption: null,
    numOfSeatsOption: null,
    typeOption: null,
    regionOption: null,

    transmissionOptions: [],
    categoryOptions: [],
    envyOptions: [],
    warrantyOptions: [],
    colorOptions: [],
  });
  const [filterIsOpen, setFilterIsOpen] = useState(false);
  const [modelSelect, setModelSelect] = useState(null);
  const searchPath = useRouteConfiguration().find(el => el.name === 'SearchPage').path;

  const extraFilterRef = useRef(null);
  const filterHeight = useRef(null);

  const config = useConfiguration();
  const categories = config.listing.listingFields;
  const brands = config.categoryConfiguration.categories;

  const handleToggleFilter = () => {
    if (!filterHeight.current) {
      filterHeight.current = extraFilterRef.current.scrollHeight;
    }
    setFilterIsOpen(prev => !prev);
  };

  const handleMultiChange = (name, selectedOptions) => {
    setSelectValues(prev => ({
      ...prev,
      [name]: selectedOptions.map(option => option.value),
    }));
  };

  const handleChange = (name, selectedOption) => {
    if (name === 'engineOption') {
      setSelectValues(prev => ({
        ...prev,
        powerFromOption: null,
        powerToOption: null,
        capacityFromOption: null,
        capacityToOption: null,
      }));
    }
    if (name === 'brandOption') {
      setSelectValues(prev => ({
        ...prev,
        [name]: selectedOption,
        modelOption: null,
      }));
      setModelSelect(null);
    } else {
      setSelectValues(prev => ({
        ...prev,
        [name]: selectedOption ? selectedOption.value : null,
      }));
      if (name === 'modelOption') setModelSelect(selectedOption);
    }
  };

  const onSearch = () => {
    const params = new URLSearchParams();

    if (selectValues.brandOption) params.set('pub_categoryLevel1', selectValues.brandOption.value);
    if (selectValues.modelOption) params.set('pub_categoryLevel2', selectValues.modelOption);

    const yearFrom = selectValues.yearFromOption;
    const yearTo = selectValues.yearToOption;
    if (yearFrom || yearTo) {
      params.set(
        'pub_year',
        `${yearFrom ? yearFrom : yearConfig.minimum},${yearTo ? yearTo : yearConfig.maximum}`
      );
    }
    
    // If the km 0 option is selected, set the mileage from 0 to 5000
    if (selectValues.typeOption === 'km0') {
      params.set('pub_mileage', '0,5000');
    } else {
      const mileageFrom = selectValues.mileageFromOption;
      const mileageTo = selectValues.mileageToOption;
      if (mileageFrom || mileageTo) {
        params.set(
          'pub_mileage',
          `${mileageFrom ? mileageFrom : mileageConfig.minimum},${
            mileageTo ? mileageTo : mileageConfig.maximum
          }`
        );
      }
    }

    if (selectValues.engineOption) params.set('pub_engine', selectValues.engineOption);

    const powerFrom = selectValues.powerFromOption;
    const powerTo = selectValues.powerToOption;
    const powerType =
    selectValues.engineOption === 'electric' || selectValues.engineOption === 'hybrid'
      ? 'Power'
      : 'horsepower';
    if (powerFrom || powerTo) {
      params.set(
        `pub_${powerType}`,
        `${powerFrom ? powerFrom : powerConfig.minimum},${powerTo ? powerTo : powerConfig.maximum}`
      );
    }

    const capacityFrom = selectValues.capacityFromOption;
    const capacityTo = selectValues.capacityToOption;
    const isElectric =
      selectValues.engineOption === 'electric' || selectValues.engineOption === 'hybrid';
    if (capacityFrom || capacityTo) {
      if (isElectric) {
        params.set(
          `pub_batterycapacity`,
          `${capacityFrom ? capacityFrom : capacityConfig.minimum},${
            capacityTo ? capacityTo : capacityConfig.maximum
          }`
        );
      } else {
        const from = capacityOptions.findIndex(el => el.value === capacityFrom) || 0;
        const to =
          capacityOptions.findIndex(el => el.value === capacityTo) + 1 || capacityOptions.length;
        params.set(
          `pub_enginecapacity`,
          capacityOptions
            .slice(from, to)
            .map(el => el.value)
            .join(',')
        );
      }
    }

    if (selectValues.driveTypeOption) params.set('pub_driveType', selectValues.driveTypeOption);
    if (selectValues.priceOption) params.set('price', `0,${selectValues.priceOption}`);
    if (selectValues.numOfSeatsOption)
      params.set('pub_numberOfSeats', `${selectValues.numOfSeatsOption}`);

    // Don't set pub_type if km0 option is selected
    if (selectValues.typeOption && selectValues.typeOption !== 'km0') {
      params.set('pub_type', `${selectValues.typeOption}`);
    }

    if (selectValues.categoryOptions.length !== 0)
      params.set('pub_bodyType', selectValues.categoryOptions.join(','));
    if (selectValues.envyOptions.length !== 0)
      params.set('pub_EtiquetaAmbiental', selectValues.envyOptions.join(','));
    if (selectValues.warrantyOptions.length !== 0)
      params.set('pub_Warranty', selectValues.warrantyOptions.join(','));
    if (selectValues.colorOptions.length !== 0)
      params.set('pub_color', selectValues.colorOptions.join(','));
    if (selectValues.transmissionOptions.length !== 0)
      params.set('pub_transmission', selectValues.transmissionOptions.join(','));

    const address = selectValues.regionOption || '';
    window.location.href = `${searchPath}?${params.toString()}${address}`;
  };

  const allBrands = sortCategoriesByName(brands).map(brand => ({
    value: brand.id,
    label: brand.name,
    subcategories: brand.subcategories,
  }));
  const brandOptions = [
    {
      label: 'Las mejores marcas',
      options: allBrands.filter(brand => popularBrands.includes(brand.label)),
    },
    {
      label: 'Todas las marcas',
      options: allBrands.filter(brand => !popularBrands.includes(brand.label)),
    },
  ];

  const modelOptions = selectValues.brandOption
    ? sortCategoriesByName(selectValues.brandOption.subcategories).map(model => ({
        value: model.id,
        label: model.name,
      }))
    : null;

  const [yearConfig, yearOptions] = categoryToRangeOptions('year', 1);

  const [mileageConfig, mileageOptions] = mileageToRangeOptions();

  const engineOptions = categoryToOptions('engine');

  const transmissionOptions = categoryToOptions('transmission');

  const driveTypeOptions = categoryToOptions('driveType');

  const priceOptions = priceToRangeOptions();

  const categoryOptions = categoryToOptions('bodyType');

  const envyOptions = categoryToOptions('EtiquetaAmbiental');

  const powerArgumnets =
    selectValues.engineOption === 'electric' || selectValues.engineOption === 'hybrid'
      ? ['Power', 30, 'kW', true]
      : ['horsepower', 50, 'CV', true];

  const [powerConfig, powerOptions] =
    selectValues.engineOption === 'electric' || selectValues.engineOption === 'hybrid'
      ? categoryToRangeOptions('Power', 30, 'kW', true)
      : powerToRangeOptions();

  const capacityArgumnets =
    selectValues.engineOption === 'electric' || selectValues.engineOption === 'hybrid'
      ? ['batterycapacity', 25, 'kWh', true]
      : ['enginecapacity', 1, 'L', true];

  let [capacityConfig, capacityOptions] =
    selectValues.engineOption === 'electric' || selectValues.engineOption === 'hybrid'
      ? capacityToRangeOptions('batterycapacity', 25, 'kWh', true)
      : [null, categoryToOptions('enginecapacity')];

  capacityOptions =
    selectValues.engineOption !== 'electric' && selectValues.engineOption !== 'hybrid'
      ? categoryToOptions('enginecapacity')
      : capacityOptions;

  const seatsOptions = categoryToOptions('numberOfSeats');
  const warrantyOptions = categoryToOptions('Warranty');
  const colorOptions = categoryToOptions('color');

  const km0Option = { value: 'km0', label: 'Km 0' };
  const typeOptionsOriginal = categoryToOptions('type');
  const typeOptions = [...typeOptionsOriginal, km0Option];

  return (
    <div className={css['filter-container']}>
      <div className={css.filter__wrapper}>
        <div className={css.filter}>
          <Select
            onChange={selectedOption => handleChange('brandOption', selectedOption)}
            placeholder="Marca"
            isSearchable={true}
            options={brandOptions}
            classNamePrefix="select"
            className="select-container"
          />
          <Select
            onChange={selectedOption => handleChange('modelOption', selectedOption)}
            value={modelSelect}
            placeholder="Modelo"
            isSearchable={false}
            isDisabled={!selectValues.brandOption}
            options={modelOptions}
            classNamePrefix="select"
            className="select-container"
          />

          <YearFilter
            handleChange={handleChange}
            selectValues={selectValues}
            yearOptions={yearOptions}
            yearConfig={yearConfig}
          />
          <MileageFilter
            handleChange={handleChange}
            selectValues={selectValues}
            mileageOptions={mileageOptions}
            mileageConfig={mileageConfig}
          />

          <Select
            onChange={selectedOption => handleChange('engineOption', selectedOption)}
            classNamePrefix="select"
            className="select-container"
            placeholder="Motor"
            options={engineOptions}
            isSearchable={false}
          />

          <Select
            isMulti
            name="Tranmission"
            onChange={selectedOption => handleMultiChange('transmissionOptions', selectedOption)}
            classNamePrefix="select"
            className="select-container"
            placeholder="Transmisión"
            options={transmissionOptions}
            isSearchable={false}
            isClearable={false}
            styles={checkoutStyles}
            closeMenuOnSelect={false}
          />
        </div>

        <div
          style={{
            height: filterIsOpen ? filterHeight.current : '0px',
            opacity: filterIsOpen ? '1' : '0',
            pointerEvents: filterIsOpen ? 'all' : 'none',
            marginTop: filterIsOpen ? '16px' : '0',
          }}
          ref={extraFilterRef}
          className={css.filter}
        >
          <PowerFilter
            selectValues={selectValues}
            handleChange={handleChange}
            powerConfig={powerConfig}
            powerOptions={powerOptions}
            isDisabled={!selectValues.engineOption}
            label={powerArgumnets[2]}
          />
          <CapacityFilter
            selectValues={selectValues}
            handleChange={handleChange}
            powerConfig={capacityConfig}
            powerOptions={capacityOptions}
            isDisabled={!selectValues.engineOption}
            label={capacityArgumnets[2]}
            isSelectedFormat={true}
            placeholders={true}
          />
          <div className="price__select">
            <CreatableSelect
              onChange={selectedOption => handleChange('priceOption', selectedOption)}
              classNamePrefix="select"
              className="select-container"
              placeholder="Precio hasta"
              options={priceOptions}
              formatCreateLabel={inputValue => `${inputValue} €`}
            />
            <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 20C4.477 20 0 15.523 0 10C0 4.477 4.477 0 10 0C15.523 0 20 4.477 20 10C20 15.523 15.523 20 10 20ZM10 18C12.1217 18 14.1566 17.1571 15.6569 15.6569C17.1571 14.1566 18 12.1217 18 10C18 7.87827 17.1571 5.84344 15.6569 4.34315C14.1566 2.84285 12.1217 2 10 2C7.87827 2 5.84344 2.84285 4.34315 4.34315C2.84285 5.84344 2 7.87827 2 10C2 12.1217 2.84285 14.1566 4.34315 15.6569C5.84344 17.1571 7.87827 18 10 18ZM8.05 9H13V11H8.05C8.13663 11.4253 8.3325 11.8208 8.61835 12.1474C8.9042 12.4741 9.27017 12.7207 9.68025 12.8629C10.0903 13.0052 10.5304 13.0383 10.9571 12.9589C11.3839 12.8794 11.7826 12.6903 12.114 12.41L13.814 13.543C13.2298 14.1789 12.475 14.6333 11.6397 14.852C10.8043 15.0707 9.92371 15.0445 9.10288 14.7764C8.28204 14.5083 7.55568 14.0097 7.01045 13.3401C6.46523 12.6705 6.12418 11.8581 6.028 11H5V9H6.027C6.12285 8.14144 6.46382 7.32861 7.00916 6.6586C7.55451 5.98859 8.28119 5.48972 9.10242 5.22157C9.92364 4.95342 10.8047 4.92732 11.6404 5.14639C12.476 5.36546 13.2309 5.82045 13.815 6.457L12.114 7.59C11.7826 7.30972 11.3839 7.12056 10.9571 7.04114C10.5304 6.96172 10.0903 6.99478 9.68025 7.13706C9.27017 7.27933 8.9042 7.52593 8.61835 7.85258C8.3325 8.17923 8.13663 8.57467 8.05 9Z" />
            </svg>
          </div>

          <Select
            isMulti
            name="Categories"
            options={categoryOptions}
            onChange={values => handleMultiChange('categoryOptions', values)}
            placeholder="Categoría"
            className="select-container"
            classNamePrefix="select"
            isSearchable={true}
            isClearable={false}
            styles={checkoutStyles}
            closeMenuOnSelect={false}
          />
          <Select
            onChange={selectedOption => handleChange('driveTypeOption', selectedOption)}
            classNamePrefix="select"
            className="select-container"
            placeholder="Tipo de Tracción"
            options={driveTypeOptions}
            isSearchable={false}
          />
          <Select
            isMulti
            name="Environmental labels"
            onChange={values => handleMultiChange('envyOptions', values)}
            options={envyOptions}
            placeholder="Etiqueta Ambiental"
            className="select-container"
            classNamePrefix="select"
            isSearchable={false}
            isClearable={false}
            styles={checkoutStyles}
            closeMenuOnSelect={false}
          />
          <Select
            onChange={selectedOption => handleChange('numOfSeatsOption', selectedOption)}
            classNamePrefix="select"
            className="wide-select-container"
            placeholder="Número de asientos"
            options={seatsOptions}
            isSearchable={false}
          />
          <Select
            isMulti
            name="Warranty"
            onChange={values => handleMultiChange('warrantyOptions', values)}
            options={warrantyOptions}
            placeholder="Garantía"
            className="wide-select-container"
            classNamePrefix="select"
            isSearchable={false}
            isClearable={false}
            styles={checkoutStyles}
            closeMenuOnSelect={false}
          />
          <Select
            isMulti
            name="Color"
            onChange={values => handleMultiChange('colorOptions', values)}
            options={colorOptions}
            placeholder="Color"
            className="wide-select-container"
            classNamePrefix="select"
            isSearchable={true}
            isClearable={false}
            styles={checkoutStyles}
            closeMenuOnSelect={false}
          />
          <Select
            onChange={selectedOption => handleChange('typeOption', selectedOption)}
            classNamePrefix="select"
            className="select-container"
            placeholder="Usado o nuevo"
            options={typeOptions}
            isSearchable={false}
          />
          <Select
            onChange={selectedOption => handleChange('regionOption', selectedOption)}
            classNamePrefix="select"
            className="select-container"
            placeholder="Región"
            options={regionOptions}
            isSearchable={true}
          />
        </div>

        <div className={css.filter__bottom}>
          <ExtendedFilterBtn handleToggleFilter={handleToggleFilter} filterIsOpen={filterIsOpen} />
          <button onClick={onSearch} className={css.searchBtn}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="50px" height="50px">
              <path d="M 21 3 C 11.601563 3 4 10.601563 4 20 C 4 29.398438 11.601563 37 21 37 C 24.355469 37 27.460938 36.015625 30.09375 34.34375 L 42.375 46.625 L 46.625 42.375 L 34.5 30.28125 C 36.679688 27.421875 38 23.878906 38 20 C 38 10.601563 30.398438 3 21 3 Z M 21 7 C 28.199219 7 34 12.800781 34 20 C 34 27.199219 28.199219 33 21 33 C 13.800781 33 8 27.199219 8 20 C 8 12.800781 13.800781 7 21 7 Z" />
            </svg>
            Mostrar Anuncios
          </button>
        </div>
      </div>
    </div>
  );
};

export default SectionFilter;
